import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, UsersIcon, ClipboardListIcon, TrendingUpIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EyeIcon, ShieldIcon, DollarSignIcon, AlertCircleIcon } from 'lucide-react';
import { mockListings, mockUsers, formatCurrency, formatDate, licensePlans } from '../utils/mockData';
const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'listings' | 'users' | 'revenue'>('overview');
  const [selectedListing, setSelectedListing] = useState<string | null>(null);
  // Statistics
  const totalListings = mockListings.length;
  const pendingListings = mockListings.filter(l => l.status === 'pending').length;
  const approvedListings = mockListings.filter(l => l.status === 'approved').length;
  const rejectedListings = mockListings.filter(l => l.status === 'rejected').length;
  const totalUsers = mockUsers.filter(u => u.role !== 'admin').length;
  const activeUsers = mockUsers.filter(u => u.isActive && u.role !== 'admin').length;
  const usersWithLicense = mockUsers.filter(u => u.license && u.role !== 'admin').length;
  const totalRevenue = mockUsers.filter(u => u.license).reduce((sum, u) => {
    const plan = licensePlans.find(p => p.id === u.license?.licenseId);
    return sum + (plan?.price || 0);
  }, 0);
  const handleApproveListing = (listingId: string) => {
    console.log('Approve listing:', listingId);
    alert('Listing approved successfully!');
  };
  const handleRejectListing = (listingId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      console.log('Reject listing:', listingId, 'Reason:', reason);
      alert('Listing rejected with reason provided to owner.');
    }
  };
  const handleToggleUserStatus = (userId: string) => {
    console.log('Toggle user status:', userId);
    alert('User status updated!');
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-295">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-296">
        {/* Header */}
        <div className="mb-8" data-id="element-297">
          <div className="flex items-center mb-2" data-id="element-298">
            <ShieldIcon className="h-8 w-8 text-primary mr-3" data-id="element-299" />
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-300">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-gray-600" data-id="element-301">
            Manage listings, users, and platform operations
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden" data-id="element-302">
          <div className="flex border-b border-gray-200" data-id="element-303">
            <button onClick={() => setActiveTab('overview')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'overview' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-304">
              <div className="flex items-center justify-center" data-id="element-305">
                <TrendingUpIcon className="h-5 w-5 mr-2" data-id="element-306" />
                Overview
              </div>
              {activeTab === 'overview' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-307"></div>}
            </button>
            <button onClick={() => setActiveTab('listings')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'listings' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-308">
              <div className="flex items-center justify-center" data-id="element-309">
                <ClipboardListIcon className="h-5 w-5 mr-2" data-id="element-310" />
                Listings
                {pendingListings > 0 && <span className="ml-2 bg-yellow-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-311">
                    {pendingListings}
                  </span>}
              </div>
              {activeTab === 'listings' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-312"></div>}
            </button>
            <button onClick={() => setActiveTab('users')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'users' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-313">
              <div className="flex items-center justify-center" data-id="element-314">
                <UsersIcon className="h-5 w-5 mr-2" data-id="element-315" />
                Users
              </div>
              {activeTab === 'users' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-316"></div>}
            </button>
            <button onClick={() => setActiveTab('revenue')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'revenue' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-317">
              <div className="flex items-center justify-center" data-id="element-318">
                <DollarSignIcon className="h-5 w-5 mr-2" data-id="element-319" />
                Revenue
              </div>
              {activeTab === 'revenue' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-320"></div>}
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-321">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-322">
              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-323">
                <div className="flex items-center justify-between mb-4" data-id="element-324">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-325">
                    <ClipboardListIcon className="h-6 w-6 text-primary" data-id="element-326" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-327">
                    Total Listings
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-328">
                  {totalListings}
                </div>
                <div className="mt-2 flex items-center text-sm" data-id="element-329">
                  <span className="text-green-600 font-medium" data-id="element-330">
                    {approvedListings} approved
                  </span>
                  <span className="text-gray-400 mx-2" data-id="element-331">•</span>
                  <span className="text-yellow-600 font-medium" data-id="element-332">
                    {pendingListings} pending
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-333">
                <div className="flex items-center justify-between mb-4" data-id="element-334">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-335">
                    <UsersIcon className="h-6 w-6 text-green-600" data-id="element-336" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-337">
                    Total Users
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-338">
                  {totalUsers}
                </div>
                <div className="mt-2 flex items-center text-sm" data-id="element-339">
                  <span className="text-green-600 font-medium" data-id="element-340">
                    {activeUsers} active
                  </span>
                  <span className="text-gray-400 mx-2" data-id="element-341">•</span>
                  <span className="text-gray-600" data-id="element-342">
                    {usersWithLicense} licensed
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-343">
                <div className="flex items-center justify-between mb-4" data-id="element-344">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-345">
                    <DollarSignIcon className="h-6 w-6 text-purple-600" data-id="element-346" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-347">
                    Revenue
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-348">
                  {formatCurrency(totalRevenue)}
                </div>
                <div className="mt-2 text-sm text-gray-600" data-id="element-349">
                  From {usersWithLicense} licenses
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-350">
                <div className="flex items-center justify-between mb-4" data-id="element-351">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center" data-id="element-352">
                    <ClockIcon className="h-6 w-6 text-yellow-600" data-id="element-353" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-354">
                    Pending Review
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-355">
                  {pendingListings}
                </div>
                <div className="mt-2 text-sm text-yellow-600 font-medium" data-id="element-356">
                  Requires attention
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-357">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-358">
                Recent Listings
              </h2>
              <div className="space-y-4" data-id="element-359">
                {mockListings.slice(0, 5).map(listing => <div key={listing.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0" data-id="element-360">
                    <div className="flex items-center space-x-4" data-id="element-361">
                      <img src={listing.images[0]} alt={listing.title} className="w-16 h-16 rounded-lg object-cover" data-id="element-362" />
                      <div data-id="element-363">
                        <h3 className="font-medium text-gray-900" data-id="element-364">
                          {listing.title}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-365">
                          {listing.location}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4" data-id="element-366">
                      <span className="text-sm font-medium text-gray-900" data-id="element-367">
                        {formatCurrency(listing.price)}
                      </span>
                      {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full" data-id="element-368">
                          <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-369" />
                          Approved
                        </span>}
                      {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full" data-id="element-370">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-371" />
                          Pending
                        </span>}
                      {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full" data-id="element-372">
                          <XCircleIcon className="h-3 w-3 mr-1" data-id="element-373" />
                          Rejected
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}

        {/* Listings Tab */}
        {activeTab === 'listings' && <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-374">
            <div className="p-6 border-b border-gray-200" data-id="element-375">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-376">
                All Listings
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-377">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-378">
                <thead className="bg-gray-50" data-id="element-379">
                  <tr data-id="element-380">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-381">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-382">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-383">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-384">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-385">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-386">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-387">
                  {mockListings.map(listing => <tr key={listing.id} className="hover:bg-gray-50" data-id="element-388">
                      <td className="px-6 py-4" data-id="element-389">
                        <div className="flex items-center" data-id="element-390">
                          <img src={listing.images[0]} alt={listing.title} className="w-12 h-12 rounded-lg object-cover" data-id="element-391" />
                          <div className="ml-4" data-id="element-392">
                            <div className="text-sm font-medium text-gray-900" data-id="element-393">
                              {listing.title}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-394">
                              {listing.location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-395">
                        <div className="text-sm text-gray-900" data-id="element-396">
                          {listing.owner.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-397">
                          {listing.owner.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-398">
                        <div className="text-sm font-medium text-gray-900" data-id="element-399">
                          {formatCurrency(listing.price)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-400">
                        {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full w-fit" data-id="element-401">
                            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-402" />
                            Approved
                          </span>}
                        {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full w-fit" data-id="element-403">
                            <ClockIcon className="h-3 w-3 mr-1" data-id="element-404" />
                            Pending
                          </span>}
                        {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full w-fit" data-id="element-405">
                            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-406" />
                            Rejected
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-407">
                        {formatDate(listing.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-408">
                        <div className="flex justify-end space-x-2" data-id="element-409">
                          <Link to={`/listing/${listing.id}`} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-410">
                            <EyeIcon className="h-5 w-5" data-id="element-411" />
                          </Link>
                          {listing.status === 'pending' && <>
                              <button onClick={() => handleApproveListing(listing.id)} className="text-green-600 hover:text-green-900" title="Approve" data-id="element-412">
                                <CheckCircleIcon className="h-5 w-5" data-id="element-413" />
                              </button>
                              <button onClick={() => handleRejectListing(listing.id)} className="text-red-600 hover:text-red-900" title="Reject" data-id="element-414">
                                <XCircleIcon className="h-5 w-5" data-id="element-415" />
                              </button>
                            </>}
                          {listing.status === 'rejected' && <button className="text-yellow-600 hover:text-yellow-900" title="View rejection reason" onClick={() => alert(listing.rejectionReason)} data-id="element-416">
                              <AlertCircleIcon className="h-5 w-5" data-id="element-417" />
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* Users Tab */}
        {activeTab === 'users' && <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-418">
            <div className="p-6 border-b border-gray-200" data-id="element-419">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-420">All Users</h2>
            </div>
            <div className="overflow-x-auto" data-id="element-421">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-422">
                <thead className="bg-gray-50" data-id="element-423">
                  <tr data-id="element-424">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-425">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-426">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-427">
                      License
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-428">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-429">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-430">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-431">
                  {mockUsers.filter(u => u.role !== 'admin').map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-432">
                        <td className="px-6 py-4" data-id="element-433">
                          <div className="flex items-center" data-id="element-434">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-435">
                              <UsersIcon className="h-5 w-5 text-gray-500" data-id="element-436" />
                            </div>
                            <div className="ml-4" data-id="element-437">
                              <div className="text-sm font-medium text-gray-900" data-id="element-438">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-439">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-440">
                          <span className="px-2 py-1 text-xs font-medium rounded-full capitalize bg-blue-100 text-blue-800" data-id="element-441">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-442">
                          {user.license ? <div className="text-sm" data-id="element-443">
                              <div className="font-medium text-gray-900 capitalize" data-id="element-444">
                                {licensePlans.find(p => p.id === user.license?.licenseId)?.planName}
                              </div>
                              <div className="text-gray-500" data-id="element-445">
                                {user.license.usedListings}/
                                {user.license.maxListings} used
                              </div>
                            </div> : <span className="text-sm text-gray-500" data-id="element-446">
                              No license
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-447">
                          {user.isActive ? <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full w-fit" data-id="element-448">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-449" />
                              Active
                            </span> : <span className="flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full w-fit" data-id="element-450">
                              <XCircleIcon className="h-3 w-3 mr-1" data-id="element-451" />
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-452">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-453">
                          <button onClick={() => handleToggleUserStatus(user.id)} className={`${user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`} data-id="element-454">
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && <div className="space-y-6" data-id="element-455">
            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-456">
              <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-457">
                Revenue Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-458">
                {licensePlans.map(plan => {
              const planUsers = mockUsers.filter(u => u.license?.licenseId === plan.id);
              const planRevenue = planUsers.length * plan.price;
              return <div key={plan.id} className="border border-gray-200 rounded-lg p-6" data-id="element-459">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-460">
                        {plan.planName}
                      </h3>
                      <div className="text-3xl font-bold text-primary mb-4" data-id="element-461">
                        {formatCurrency(planRevenue)}
                      </div>
                      <div className="space-y-2 text-sm text-gray-600" data-id="element-462">
                        <div className="flex justify-between" data-id="element-463">
                          <span data-id="element-464">Active subscriptions:</span>
                          <span className="font-medium text-gray-900" data-id="element-465">
                            {planUsers.length}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-466">
                          <span data-id="element-467">Price per month:</span>
                          <span className="font-medium text-gray-900" data-id="element-468">
                            {formatCurrency(plan.price)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-469">
                          <span data-id="element-470">Max listings:</span>
                          <span className="font-medium text-gray-900" data-id="element-471">
                            {plan.maxListings}
                          </span>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-472">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-473">
                Total Platform Revenue
              </h2>
              <div className="text-4xl font-bold text-primary mb-2" data-id="element-474">
                {formatCurrency(totalRevenue)}
              </div>
              <p className="text-gray-600" data-id="element-475">
                From {usersWithLicense} active license subscriptions
              </p>
            </div>
          </div>}
      </div>
    </div>;
};
export default AdminDashboardPage;